import { Component, OnInit, Input, Output, EventEmitter, Optional, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ITaxReturn } from "../../../../app/tax-returns/tax-return/model/i-tax-return";
import { ITaxReturnPackageGeneration } from "../../../../app/tax-returns/tax-return/model/i-tax-return-package-generation";
import { TaxReturnPackageGeneration, TaxReturnService, TaxReturnTemplate, TemplateTypeService, TemplateType, ListOfTemplateType } from "../../../../../swaggerServices";
import { ConfigStateService } from "../../../../app/config/config-state.service"
import { Tenant } from "../../../types/tenant.enum";
import { B1_QUESTION_TAG, FormType, IR21_QUESTION_TAG} from "../../../types/form.enum";
import { UserInfoService } from "../../../../app/user-info.service";

export type GenerateType = "DRAFT" | "DRAFT_WITH_TRANSITION" | "FINAL_REVIEW_AUTO" | "FINAL_REVIEW_MANUAL" | "STANDARD";

interface ITemplateSelectDialogData {
  taxReturn: ITaxReturn;
  generateType: GenerateType;
}

@Component({
    selector: "template-generation-selection",
    templateUrl: "./template-generation-selection.component.html",
    styleUrls: ["./template-generation-selection.component.scss"],
    standalone: false
})
export class TemplateGenerationSelectionComponent implements OnInit {
  taxReturn: ITaxReturn;
  generateType: GenerateType;
  @Output() closed = new EventEmitter();
  @Output() generateCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  templateTypesMap: Map<string, TemplateType> = new Map();
  templates: {
    templateFlagTrue: TaxReturnTemplate[],
    templateFlagFalse: TaxReturnTemplate[]
  } = {
    templateFlagTrue: [],
    templateFlagFalse: []
  };
  templateSelected: TaxReturnTemplate[] = [];

  showSelectionModal: boolean = false;
  generateButtonFlag: boolean = false;
  isGenerating: boolean = false;
  isSuccess: boolean = false;
  isAdmin: boolean = true;
  generateDraftResult?: ITaxReturnPackageGeneration;
  showMore: boolean = false;
  labelGenerateDraft: boolean = true;
  tenants: any;
  formType: FormType;
  returnDebug: boolean = false;
  debugOther: string = '';
  debugDocx: string = '';

  dialogData: ITemplateSelectDialogData = inject(MAT_DIALOG_DATA);

  constructor(private taxReturnService: TaxReturnService,
    private userInfo: UserInfoService,
    private templateTypeService: TemplateTypeService,
    private config: ConfigStateService,
    @Optional() private dialogRef?: MatDialogRef<TemplateGenerationSelectionComponent>) {
      this.taxReturn = this.dialogData.taxReturn;
      this.generateType = this.dialogData.generateType;
    }

  ngOnInit() {
    this.tenants = Tenant;
    this.config.loadConfig().subscribe((config) => {});
    if(this.config.featureFlags.selectPackageTemplates) {
      this.templateTypeService.getTemplateTypes().subscribe(resp => {
        this.setTemplateTypesMap(resp);
        this.filterQuestions();
        this.formType = this.findFormType();
        this.showMore =  this.templates.templateFlagTrue.length === 0 || this.templates.templateFlagFalse.length === 0;
        this.generateButtonFlag = this.templates.templateFlagTrue.length === 0;
        this.showSelectionModal = true;
      })
    } else {
      this.generateTemplate(this.generateType);
    }

    if(this.config.tenant === Tenant.HK || this.config.tenant === Tenant.CN) {
      this.labelGenerateDraft = false;
    }
    this.isAdmin = !!this.userInfo.isAdmin;

  }

  updateDebug(checked){
    this.returnDebug = checked;
  }

  updateFlag(template, checked) {
    if(checked === true){
      this.templateSelected.push(template);
    } else{
      this.templateSelected = this.templateSelected.filter(v => { return v.form !== template.form })
    }

    if (this.templateSelected.length === 0) {
      this.generateButtonFlag = true;
    } else {
      this.generateButtonFlag = false;
    }
  }

  confirmSelection() {
    this.generateSelectedTemplate();
  }

  onClose(result: boolean = false) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.closed.emit();
  }

  private filterQuestions() {
    let template = <any>{};
    const questionIdExistingMapping = new Map();

    for ( const category of this.taxReturn.categories ) {
      for ( const subcategory of category.subcategories ) {
        for ( const question of subcategory.questions ) {
          questionIdExistingMapping.set(question.id, true);
        }
      }
    }

    this.taxReturn.questions.forEach(v => {
      if (
        String(v.tag)
          .toLocaleUpperCase()
          .startsWith("GENERATE_") && questionIdExistingMapping.has(v.id)
      ) {

        const templateType = v.tag.slice(9);
        template = {
          answerId: "",
          taxReturnId: this.taxReturn.id,
          form: templateType
        }

        // We only want to display valid template types here (not batch)
        if (!this.templateTypesMap.get(templateType)) {
          return;
        }

        if (v.answers.length === 1 ) {
          template.answerId = v.answers[0].id;
          if (v.answers[0].answer === "true") {
            this.generateButtonFlag = false;
            this.templates.templateFlagTrue.push({
              ...template,
              flag: true
            });
            this.templateSelected.push({
              ...template,
              flag: true
            });
          } else {
            this.templates.templateFlagFalse.push({
              ...template,
              flag: false
            });
          }
        } else if (v.answers.length === 0 ) {
          this.templates.templateFlagFalse.push({
            ...template,
            flag: false
          });
        }
      }
    });
  }

  private findFormType(): FormType {
    const b1Question = this.taxReturn.questions.find(q => q.tag === B1_QUESTION_TAG);
    const ir21Question = this.taxReturn.questions.find(q => q.tag === IR21_QUESTION_TAG);
    // Neither IRB1 nor IR21 is found for this taxReturn
    if (b1Question === undefined && ir21Question === undefined) {
      return FormType.NULL;
    }
    const typeAnswers = Object.values(this.taxReturn.answersById).filter(answer =>
      // If B1 trigger question is found and the answer to IR21 trigger question is "true";
      // @ts-ignore TS2339: Property 'answer' does not exist on type '{}'. TS2339: Property 'questionId' does not exist on type '{}'.
      (b1Question && (answer.questionId === b1Question.id && answer.answer === "true"))
      // or If IR21 trigger question is found and the answer to IR21 trigger question is "true";
      // @ts-ignore TS2339: Property 'answer' does not exist on type '{}'. TS2339: Property 'questionId' does not exist on type '{}'.
      || (ir21Question && (answer.questionId === ir21Question.id && answer.answer === "true"))
    );
    // Neither IRB1 nor IR21 is found for this taxReturn
    if (typeAnswers === undefined || typeAnswers.length === 0) {
      return FormType.NULL;
    // Both IRB1 and IR21 are found for this taxReturn
    } else if (typeAnswers.length >= 2) {
      return FormType.UNKNOWN;
    // One of the IRB1 or IR21 is found for this taxReturn
    } else if (typeAnswers.length === 1) {
      // @ts-ignore TS2339: Property 'questionId' does not exist on type '{}'.
      if (typeAnswers[0].questionId === b1Question.id) {
        return FormType.IRB1;
       } else {
        return FormType.IR21;
      }
    }
  }

  private generateSelectedTemplate() {
    this.isGenerating = true;
    if (this.generateType === "DRAFT" ) {
      this.taxReturnService.generateDraftTaxReturnPackage(
        this.taxReturn.id,
        {
          template: this.templateSelected,
          debug: this.returnDebug
        }
      ).subscribe(
        (resp: TaxReturnPackageGeneration) => {
          this.isSuccess = true;
          this.isGenerating = false;
          this.generateDraftResult = resp;
          this.debugOther = JSON.stringify(this.generateDraftResult.debug?.otherSubstitutionData, null, 2)
          this.debugDocx = JSON.stringify(this.generateDraftResult.debug?.docxSubstitutionData, null, 2)
          if (this.taxReturn.id === resp.taxReturn.id) {
            this.taxReturn.generateRobotQueueItemId = resp.taxReturn.generateRobotQueueItemId;
            this.generateCompleted.emit(true);
          }
        },
        () => this.isGenerating = false);
    } else if (this.generateType === "STANDARD" || this.generateType === "DRAFT_WITH_TRANSITION" || this.generateType === "FINAL_REVIEW_AUTO") {
      this.taxReturnService.generateTaxReturnThroughRobot(
        this.taxReturn.id,
        this.generateType,
        {
          template: this.templateSelected,
        },
      ).subscribe(
        (resp: TaxReturnPackageGeneration) => {
          this.isSuccess = true;
          this.isGenerating = false;
          this.generateDraftResult = resp;
          if (this.taxReturn.id === resp.taxReturn.id) {
            this.taxReturn.generateRobotQueueItemId = resp.taxReturn.generateRobotQueueItemId;
            this.generateCompleted.emit(true);
          }
        },
        () => this.isGenerating = false);
    }
  }

  private generateTemplate(generateType: 'DRAFT' | 'DRAFT_WITH_TRANSITION' | 'FINAL_REVIEW_AUTO' | 'FINAL_REVIEW_MANUAL' | 'STANDARD') {
    this.isGenerating = true;
    if ( generateType === "DRAFT" ) {
      this.taxReturnService.generateDraftTaxReturnPackage(this.taxReturn.id).subscribe(
        (resp: TaxReturnPackageGeneration) => {
          this.isGenerating = false;
          this.generateDraftResult = resp;
          this.isSuccess = true;
          this.showSelectionModal = true;
          if (this.taxReturn.id === resp.taxReturn.id) {
            this.taxReturn.generateRobotQueueItemId = resp.taxReturn.generateRobotQueueItemId;
            this.generateCompleted.emit(true);
          }
        },
        () => {
          this.isGenerating = false;
          this.generateCompleted.emit(false);
        });
    } else if( generateType === "STANDARD" || generateType === "DRAFT_WITH_TRANSITION" || generateType === "FINAL_REVIEW_AUTO") {
      this.taxReturnService.generateTaxReturnThroughRobot(this.taxReturn.id, this.generateType).subscribe(
        (resp: TaxReturnPackageGeneration) => {
          this.isGenerating = false;
          this.generateDraftResult = resp;
          this.isSuccess = true;
          this.showSelectionModal = true;
          if (this.taxReturn.id === resp.taxReturn.id) {
            this.taxReturn.generateRobotQueueItemId = resp.taxReturn.generateRobotQueueItemId;
            this.generateCompleted.emit(true);
          }
        },
        () => {
          this.isGenerating = false;
          this.generateCompleted.emit(false);
        });
    }
  }

  private setTemplateTypesMap(templateTypes: ListOfTemplateType) {
    const isValidTemplateType = (tt) => {
      if (this.config.tenant === Tenant.SG) {
        return true;
      }
      return tt.module && tt.module.toLocaleUpperCase() === "TAXRETURN" &&
      tt.templateGroup && tt.templateGroup.toLocaleUpperCase() === "TAXRETURN";
    }
    for (const tt of templateTypes) {
      if (isValidTemplateType(tt)) {
        this.templateTypesMap.set(tt.templateType, tt);
      }
    }
  }
}
